import {Typography} from '@/components/ui/typography';
import {Button} from '@/components/ui/button';
import {MessageCircle} from 'lucide-react';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectCommentById, selectCommentsIdsByParentId} from '@/features/commentsSlice';
import {selectUserById} from '@/features/usersSlice';
import {UserAvatar} from '@/components/UserAvatar';
import {formatName, formatRelativeDate, prepareText, sanitizeCommentText} from '@/util/format';
import {useForm} from 'react-hook-form';
import * as z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Textarea} from '@/components/ui/textarea';
import {cn} from '@/lib/utils';
import * as pluralize from 'pluralize';
import {User} from '@/types/user';

export const CommentsSection = ({comments, user, onSubmitCommentButtonClick, onSubmitReplyButtonClick, onFetchReply, showCommentsList = true}: {
    comments: string[];
    user: User;
    onSubmitCommentButtonClick: (text: string) => void;
    onSubmitReplyButtonClick: (id: string, text: string) => void;
    onFetchReply: (id: string) => void;
    showCommentsList?: boolean;
}) => {
    const [replyToCommentId, setReplyToCommentId] = React.useState<string | null>(null);
    const [activeCommentIds, setActiveCommentIds] = React.useState<{[key: string]: boolean}>({});
    const [isMainReplyFormActive, setIsMainReplyFormActive] = React.useState(false);

    return (
        <div className="mt-6 flex flex-col justify-start items-start my-4">
            <Typography variant="h3">
                Members’ reactions
            </Typography>
            <div className="pt-4 w-full">
                <Typography className="text-sm text-gray-800 mb-2">
                    {formatName(user)}, what are your thoughts?
                </Typography>
                <div className="flex w-full mb-4">
                    <ReplyForm
                        collapsed={!isMainReplyFormActive}
                        onFocus={() => {
                            setIsMainReplyFormActive(true);
                        }}
                        placeholder={"Join the conversation, share your thoughts or questions with the CaseHub community."}
                        onSubmitCommentButtonClick={(text) => {
                            onSubmitCommentButtonClick(text);
                            setIsMainReplyFormActive(false);
                        }}
                        onCancelButtonClick={() => {
                            setIsMainReplyFormActive(false);
                        }}
                    />
                </div>
            </div>
            {showCommentsList ? (
                <CommentsList
                    level={0}
                    comments={comments}
                    replyToCommentId={replyToCommentId}
                    setReplyToCommentId={setReplyToCommentId}
                    activeCommentIds={activeCommentIds}
                    onSubmitCommentButtonClick={(id, text) => {
                        if (!user) {
                            return null;
                        }
                        onSubmitReplyButtonClick(id, text);
                    }}
                    onShowRepliesButtonClick={(id) => {
                        const isVisible = !activeCommentIds[id];

                        setActiveCommentIds({
                            ...activeCommentIds,
                            [id]: isVisible,

                        });

                        if (isVisible) {
                            onFetchReply(id);
                        }
                    }}
                />
            ) : null}
        </div>
    );
}

const CommentsList = ({
    comments,
    level,
    replyToCommentId,
    onSubmitCommentButtonClick,
    onShowRepliesButtonClick,
    setReplyToCommentId,
    activeCommentIds,
    rootCommentId,
}: {
    level: number;
    comments: string[];
    replyToCommentId: string | null;
    setReplyToCommentId: (id: string | null) => void;
    onSubmitCommentButtonClick: (id: string, text: string) => void;
    onShowRepliesButtonClick: (commentId: string) => void;
    activeCommentIds: {[key: string]: boolean};
    rootCommentId?: string;
}) => {
    return (
        <div className="flex flex-col justify-start items-start w-full">
            {
                comments.map((id) => (
                    <Comment
                        key={id}
                        level={level}
                        commentId={id}
                        rootCommentId={rootCommentId}
                        activeCommentIds={activeCommentIds}
                        replyToCommentId={replyToCommentId}
                        replyFormVisible={replyToCommentId === id}
                        setReplyToCommentId={setReplyToCommentId}
                        onSubmitCommentButtonClick={onSubmitCommentButtonClick}
                        onShowRepliesButtonClick={onShowRepliesButtonClick}
                    />
                ))
            }
        </div>
    );
}

type CommentProps = {
    commentId: string;
    level: number;
    replyFormVisible: boolean;
    activeCommentIds: {[key: string]: boolean};
    replyToCommentId: string | null;
    setReplyToCommentId: (id: string | null) => void;
    onSubmitCommentButtonClick: (id: string, text: string) => void;
    onShowRepliesButtonClick: (commentId: string) => void;
    rootCommentId?: string;
}
const Comment = ({
    commentId,
    rootCommentId,
    level,
    replyToCommentId,
    activeCommentIds,
    replyFormVisible,
    setReplyToCommentId,
    onSubmitCommentButtonClick,
    onShowRepliesButtonClick
}: CommentProps) => {
    const comment = useSelector(state => selectCommentById(state, commentId));

    const childComments = useSelector(state => selectCommentsIdsByParentId(state, commentId));

    const commentAuthor = useSelector(state => {
        return selectUserById(state, comment?.user);
    });

    if (!comment || !comment.content || comment?.content?.length < 3) {
        return null;
    }

    if (level === 0 && comment.parent_id) {
        return null;
    }

    let isRepliesFormOpened = activeCommentIds[comment.id];

    return (
        <div className={cn({
            ['w-full']: true,
            ['border-l-[1px] border-gray-400']: level === 1,
            // ['py-4']: level === 1,
            'pl-8': level === 1,
        })}>
            <div className={cn({
                ['border-b-[1px] border-gray-200']: (!isRepliesFormOpened || level > 0),
                ['py-6']: true,
            })}>
                <div className={cn({
                    ['flex space-x-4 w-full']: true,
                })}>
                    <UserAvatar userId={comment.user} />
                    <div className="flex flex-col items-start w-full space-y-4">
                        <div>
                            <p className="text-sm font-medium leading-none">{formatName(commentAuthor)}</p>
                            <p className="text-xs text-muted-foreground mb-2">{formatRelativeDate(comment.created)}</p>
                            {
                                prepareText(comment.content || '').map((line: string, index: number) => (
                                    <p className="text-sm text-gray-800" key={index} dangerouslySetInnerHTML={{
                                        __html: sanitizeCommentText(line)
                                    }} />
                                ))
                            }
                        </div>
                        <div className="flex justify-between items-center w-full">
                            <div>
                                {comment.child_count > 0 ? (
                                    <Button variant="plain" size="subtle" onClick={() => {
                                        onShowRepliesButtonClick(comment.id);
                                    }}>
                                        <MessageCircle className="w-4 h-4" />
                                        {isRepliesFormOpened ? 'Hide replies' : (
                                            <>
                                                {comment.child_count}
                                                {' '}
                                                {pluralize('reply', comment.child_count)}
                                            </>
                                        )}
                                    </Button>
                                ) : null}
                            </div>
                            {!replyFormVisible && level < 2 ? (<Button variant="link" size="sm" className="font-bold" onClick={() => {
                                setReplyToCommentId(comment.id);
                            }}>Reply</Button>) : null}
                        </div>
                    </div>
                </div>
                {replyFormVisible ? (
                    <div className={cn({
                        ['mt-4']: true,
                        ['pt-4']: true,
                        ['px-6']: true,
                        ['border-l-[1px] border-gray-400']: true,
                    })}>
                        <ReplyForm
                            placeholder="Reply to this comment..."
                            onSubmitCommentButtonClick={(text) => {
                                onSubmitCommentButtonClick(rootCommentId || comment.id, text);
                                if (!isRepliesFormOpened) {
                                    onShowRepliesButtonClick(comment.id);
                                }
                                setReplyToCommentId(null);
                            }}
                            onCancelButtonClick={() => {
                                setReplyToCommentId(null);
                            }}
                        />
                    </div>
                ) : null}
            </div>
            {isRepliesFormOpened ? (
                <div className={cn({
                    // ['border-b-[1px]']: level === 0,
                })}>
                    <CommentsList
                        level={level + 1}
                        rootCommentId={rootCommentId || comment.id}
                        comments={childComments}
                        activeCommentIds={activeCommentIds}
                        replyToCommentId={replyToCommentId}
                        setReplyToCommentId={setReplyToCommentId}
                        onSubmitCommentButtonClick={onSubmitCommentButtonClick}
                        onShowRepliesButtonClick={onShowRepliesButtonClick}
                    />
                </div>
            ) : null}
        </div>
    )
}

const commentFormSchema = z.object({
    comment: z.string().min(3, {
        message: "Comment must be at least 3 characters.",
    }),
});

const ReplyForm = ({
    collapsed,
    onFocus,
    onSubmitCommentButtonClick,
    onCancelButtonClick,
    placeholder,
}: {
    collapsed?: boolean;
    onFocus?: () => void;
    onSubmitCommentButtonClick: (comment: string) => void;
    onCancelButtonClick?: () => void;
    placeholder?: string;
}) => {
    const form = useForm<z.infer<typeof commentFormSchema>>({
        resolver: zodResolver(commentFormSchema),
        defaultValues: {
            comment: "",
        },
    })

    function onSubmit(values: z.infer<typeof commentFormSchema>) {
        if (typeof onSubmitCommentButtonClick === 'function') {
            onSubmitCommentButtonClick(values.comment);
            form.reset();
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 w-full">
                <FormField
                    control={form.control}
                    name="comment"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Textarea
                                    placeholder={placeholder}
                                    onFocus={onFocus}
                                    {...field}
                                    rows={collapsed ? 1 : 5}
                                    className={cn('bg-white', {
                                        ['min-h-[10px]']: collapsed,
                                    })}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {collapsed ? null : (
                    <div className="flex w-full justify-end items-center gap-1">
                        <Button type="button" size="sm" variant="ghost" onClick={onCancelButtonClick}>Cancel</Button>
                        <Button type="submit" size="sm">Respond</Button>
                    </div>
                )}
            </form>
        </Form>
    )
}
